import { Link } from 'react-router-dom';
import type { RangeDay } from '../lib/api';

// CalendarDayCell — single date in the month grid. Letter codes the prescribed
// session, today wears a 2px ink ring, and how filled-in the cell looks is how
// close it is to now. Tapping a past/future day routes to
// /training?session=<id> for preview.
//
// `placeholder` renders an empty slot used for the leading days before the 1st
// of a month so the 7-column grid stays aligned to Monday-start weeks.
export function CalendarDayCell({
  day,
  isToday,
  inFuture,
  isPreBet,
  isCompleted,
}: {
  day: RangeDay;
  isToday: boolean;
  inFuture: boolean;
  isPreBet?: boolean;
  isCompleted?: boolean;
}) {
  const dayNum = day.date.slice(8, 10);
  const letter = sessionLetter(day.session.type);
  const isRest = day.session.type === 'rest';
  // A completed *rest* day isn't a "workout done" — still show the cell, just
  // skip the green styling. Avoids confusing "✓" badges on rest cells.
  const showDone = Boolean(isCompleted) && !isRest;

  // Today is the only cell that gets the ink ring — the same black that marks
  // every selected/active thing in the app. A finished session gets a green
  // edge to match its fill; everything else takes the plain hairline.
  const ringClass = isToday
    ? 'ring-2 ring-accent ring-offset-2 ring-offset-bg-raised'
    : showDone
      ? 'border border-good/25 hover:border-good/50'
      : 'border border-line';

  // Recede is carried by the FILL, never by an `opacity` wash. `opacity`
  // composites the whole cell — glyphs included — toward the white card behind
  // it, which drops the date to 2.7:1 and the session letter to 3.2:1; no
  // opacity that actually dims anything can clear 4.5:1 here. So the well gets
  // lighter the further a day is from "now" and the ink stays ink:
  //   pre-bet  → no fill at all, an empty outlined slot
  //   future   → the whisper panel, "not filled in yet"
  //   past/now → the grey well
  // Hover lives on the fill (not the shared class list) so it can't wash the
  // green out of a completed cell — hover variants win over base utilities
  // regardless of the order they're written in.
  const bgClass = showDone
    ? 'bg-good-soft'
    : isPreBet
      ? 'hover:bg-fg/[0.06]'
      : inFuture && !isToday
        ? 'bg-bg-raised-2 hover:bg-fg/[0.06]'
        : 'bg-bg-sunken hover:bg-fg/[0.12]';

  const ariaLabel = `${day.date} — ${day.session.title}${showDone ? ' (completed)' : ''}`;

  return (
    <Link
      to={`/training?session=${encodeURIComponent(day.session.id)}`}
      aria-label={ariaLabel}
      className={`relative flex flex-col items-center justify-center aspect-square rounded-control transition-colors ${bgClass} ${ringClass}`}
    >
      {/* On the green pastel the date takes the family ink — `fg-faint` on
          `good-soft` is 4.39:1 and misses the floor. */}
      <span
        className={`absolute top-1 left-1.5 font-mono text-[10px] tabular-nums leading-none ${
          showDone ? 'text-good' : 'text-fg-faint'
        }`}
      >
        {dayNum}
      </span>
      {/* A session still ahead of you is violet — the system's "planned, not
          yet actual" mark. Today and everything behind it is ink; a pre-bet day
          is grey, because it was never part of the plan. */}
      <span
        className={`font-mono text-base font-medium leading-none ${
          letter === '·' || isPreBet
            ? 'text-fg-faint'
            : inFuture && !isToday
              ? 'text-pop'
              : 'text-fg'
        }`}
        aria-hidden
      >
        {letter}
      </span>
      {showDone && (
        <span
          aria-hidden
          className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center size-3.5 rounded-full bg-good text-white"
        >
          <svg viewBox="0 0 12 12" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 6.5l2.5 2.5 4.5-5" />
          </svg>
        </span>
      )}
    </Link>
  );
}

export function CalendarDayPlaceholder() {
  return <div className="aspect-square rounded-control bg-transparent" aria-hidden />;
}

// sessionLetter maps a session type to a single-character glyph for the cell.
// Squat (S), Hinge (H), Upper (U), Conditioning (C), Walk (W), Easy (E),
// Rest (·, mid-dot), Mobility (M). Falls back to first uppercase letter.
export function sessionLetter(type: string): string {
  switch (type) {
    case 'lower_squat':
      return 'S';
    case 'lower_hinge':
      return 'H';
    case 'upper':
      return 'U';
    case 'conditioning':
      return 'C';
    case 'long_walk':
      return 'W';
    case 'easy_conditioning':
      return 'E';
    case 'rest':
      return '·';
    case 'mobility':
      return 'M';
    default:
      if (!type) return '?';
      return type.charAt(0).toUpperCase();
  }
}
